import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { getCategoryTree, getUncategorisedProducts } from '../../services/categoriesApi';

const flatten = (nodes, acc = []) => {
  for (const n of nodes) {
    acc.push(n);
    if (n.children?.length) flatten(n.children, acc);
  }
  return acc;
};

const CategorisationDashboard = () => {
  const [tree, setTree] = useState(null);
  const [uncatTotal, setUncatTotal] = useState(null);
  const [holdingCode, setHoldingCode] = useState('nocat');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [t, u] = await Promise.all([
          getCategoryTree(),
          getUncategorisedProducts({ limit: 1, offset: 0 }),
        ]);
        if (cancelled) return;
        setTree(t);
        setUncatTotal(u.total);
        setHoldingCode(u.holding_bucket_code);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const stats = useMemo(() => {
    if (!tree) return null;
    const all = flatten(tree);
    const leaves = all.filter((n) => !n.children?.length);
    const top10 = [...all]
      .sort((a, b) => (b.product_count || 0) - (a.product_count || 0))
      .slice(0, 10);
    const totalAssignments = all.reduce((s, n) => s + (n.product_count || 0), 0);
    return {
      nodeCount: all.length,
      leafCount: leaves.length,
      totalAssignments,
      top10,
    };
  }, [tree]);

  if (error) return <div className="error">Error: {error}</div>;
  if (!tree || !stats) return <div>Loading categorisation dashboard…</div>;

  return (
    <div className="sync-dashboard">
      <header>
        <h1>Categorisation</h1>
        <span className="muted">
          Source of truth: Akeneo &quot;Waterco&quot; category tree (root omitted in UI).
        </span>
      </header>

      <section className="card">
        <h2>Snapshot</h2>
        <table>
          <tbody>
            <tr>
              <th style={{ textAlign: 'left' }}>Categories in tree</th>
              <td>{stats.nodeCount.toLocaleString()}</td>
            </tr>
            <tr>
              <th style={{ textAlign: 'left' }}>Leaf categories</th>
              <td>{stats.leafCount.toLocaleString()}</td>
            </tr>
            <tr>
              <th style={{ textAlign: 'left' }}>Total SKU↔category assignments</th>
              <td>{stats.totalAssignments.toLocaleString()}</td>
            </tr>
            <tr>
              <th style={{ textAlign: 'left' }}>
                SKUs in <code>{holdingCode}</code> / no category
              </th>
              <td>
                <Link to="/products/categorisation/no-category">
                  <strong>{uncatTotal?.toLocaleString() ?? '—'}</strong> →
                </Link>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="card">
        <h2>Top 10 most-used categories</h2>
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Label</th>
              <th style={{ textAlign: 'right' }}>SKUs</th>
            </tr>
          </thead>
          <tbody>
            {stats.top10.map((n) => (
              <tr key={n.code}>
                <td>
                  <code>{n.code}</code>
                  {n.is_holding_area && <span className="status" style={{ marginLeft: 6 }}>holding</span>}
                </td>
                <td>{n.label}</td>
                <td style={{ textAlign: 'right' }}>{(n.product_count || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card">
        <h2>Tools</h2>
        <ul>
          <li>
            <Link to="/products/categorisation/tree">Browse the Akeneo Waterco tree →</Link>
          </li>
          <li>
            <Link to="/products/categorisation/no-category">No-category queue (drain target) →</Link>
          </li>
          <li>
            <Link to="/products/categorisation/bulk">Bulk categorise SKUs →</Link>
          </li>
        </ul>
      </section>
    </div>
  );
};

export default CategorisationDashboard;
